import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Tooltip
} from '@heroui/react';
import { OpenNewWindow, Xmark } from 'iconoir-react';
import { Link } from 'react-router-dom';

import { DeploymentAlertsSection } from './DeploymentAlertsSection';
import { DeploymentSummarySection } from './DeploymentSummarySection';
import {
  DeploymentTriggerSection,
  TriggerSourceItem
} from './DeploymentTriggerSection';

import i18n from '~/common/helpers/i18n';
import { Deployment } from '~/entities/Deployment';
import { Trigger } from '~/entities/Trigger';

export type DetailsDeploymentDrawerProps = {
  onCancel: (value?: boolean) => void;
  title?: string;
  deployment: Deployment;
  trigger: Trigger;
  linkGoToPipeline: string;
  triggerSource?: TriggerSourceItem[];
  redeploy: (deployment: Deployment['pipeline']) => void;
};

/**
 * The deployment details sidesheet, on HeroUI and tailwind instead of beehive and
 * styled-components. Same content, same order: summary, replica counters, trigger, problem alerts.
 *
 * Shell follows the HeroUI drawers already in the portal — Build's capsule pipelines and
 * globals/accounts sidesheets, Monitor's agent log details: `size='3xl'`, actions in the top right
 * of a bordered `DrawerHeader` with the title under them, and a scrolling body of bordered panels.
 *
 * Behind the `run_deployment_details_heroui` flag — while it is off the beehive
 * `DetailsDeploymentWithServerless` still renders.
 */
export const DetailsDeploymentDrawer = ({
  onCancel,
  deployment,
  trigger,
  linkGoToPipeline,
  triggerSource,
  redeploy
}: DetailsDeploymentDrawerProps) => {
  const onRedeploy = () => {
    onCancel();
    redeploy(deployment?.pipeline);
  };

  return (
    <Drawer
      isOpen
      onClose={() => onCancel(false)}
      hideCloseButton
      placement='right'
      size='3xl'
      aria-label={i18n.t('scenes.runtime.labels.details_deployment') || ''}
      data-testid='run-deployment-details-drawer'
      classNames={{
        // Same stacking every other HeroUI drawer in the portal uses, so the app chrome does not
        // paint over it.
        backdrop: 'z-[3000]',
        wrapper: 'z-[3001]'
      }}
    >
      <DrawerContent>
        <div className='flex h-full flex-col'>
          <DrawerHeader className='flex flex-col gap-2 border-b border-outline px-6 py-4'>
            <div className='flex justify-end'>
              <div className='flex items-center gap-1'>
                <Tooltip content={i18n.t('action.open_pipeline')}>
                  <Button
                    isIconOnly
                    as={Link}
                    variant='light'
                    size='sm'
                    target='_blank'
                    to={`${linkGoToPipeline}`}
                    aria-label={i18n.t('action.open_pipeline') || ''}
                    className='h-8 w-8 min-w-8 rounded-lg text-default-500 hover:bg-default-100 hover:text-foreground'
                    data-testid='run-page-detail-open-new-pipeline'
                  >
                    <OpenNewWindow className='icon-smallest' />
                  </Button>
                </Tooltip>
                <Button
                  isIconOnly
                  variant='light'
                  size='sm'
                  onPress={() => onCancel(false)}
                  aria-label={i18n.t('action.close') || ''}
                  className='h-8 w-8 min-w-8 rounded-lg text-default-500 hover:bg-default-100 hover:text-foreground'
                  data-testid='run-deployment-details-drawer-close'
                >
                  <Xmark className='icon-smallest' />
                </Button>
              </div>
            </div>
            <h2 className='text-lg font-semibold text-on-surface'>
              {i18n.t('scenes.runtime.labels.details_deployment')}
            </h2>
          </DrawerHeader>

          <DrawerBody className='overflow-y-auto px-6 py-4'>
            <div className='flex flex-col gap-4'>
              <DeploymentSummarySection deployment={deployment} />
              <DeploymentTriggerSection
                deployment={deployment}
                trigger={trigger}
                triggerSource={triggerSource}
              />
              <DeploymentAlertsSection
                deployment={deployment}
                onRedeploy={onRedeploy}
              />
            </div>
          </DrawerBody>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DetailsDeploymentDrawer;
